import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, inject, OnDestroy, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyleKit } from '@tiptap/extension-text-style';

@Component({
  selector: 'app-rich-text-editor',
  templateUrl: './ck-classic-editor.component.html',
  styleUrl: './ck-classic-editor.component.scss',
  providers: [
    { provide: NG_VALUE_ACCESSOR, multi: true, useExisting: RichTextEditorComponent }
  ]
})
export class RichTextEditorComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  private changeDetector = inject(ChangeDetectorRef);

  @ViewChild('editor', { static: true }) private editorElement!: ElementRef<HTMLDivElement>;

  editor?: Editor;
  private value = '';
  private isDisabled = false;
  showMoreTools = false;
  activeColorPicker: 'text' | 'background' | null = null;

  private onValueChange = (_: string) => { };
  private onTouched = () => { };

  ngAfterViewInit(): void {
    this.editor = new Editor({
      element: this.editorElement.nativeElement,
      extensions: [
        StarterKit,
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        TextStyleKit,
      ],
      content: this.value,
      editable: !this.isDisabled,
      onUpdate: ({ editor }) => {
        this.value = editor.getHTML();
        this.onValueChange(this.value);
      },
      onBlur: () => this.onTouched(),
      onTransaction: () => this.refreshToolbar(),
      onSelectionUpdate: () => this.refreshToolbar(),
    });
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
  }

  writeValue(htmlText: string | null): void {
    this.value = htmlText ?? '';
    if (this.editor && this.editor.getHTML() !== this.value) {
      this.editor.commands.setContent(this.value, { emitUpdate: false });
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onValueChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.editor?.setEditable(!isDisabled);
  }

  undo(): void {
    this.editor?.chain().focus().undo().run();
  }

  redo(): void {
    this.editor?.chain().focus().redo().run();
  }

  setHeading(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    const chain = this.editor?.chain().focus();

    if (!chain) { return; }
    if (value === 'paragraph') {
      chain.setParagraph().run();
    } else {
      chain.toggleHeading({ level: Number(value) as 1 | 2 | 3 }).run();
    }
  }

  setFontFamily(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    const chain = this.editor?.chain().focus();
    if (!chain) { return; }

    if (value === 'default') {
      chain.unsetFontFamily().run();
    } else {
      chain.setFontFamily(value).run();
    }
  }

  isActive(format: 'bold' | 'italic' | 'underline'): boolean {
    return this.editor?.isActive(format) ?? false;
  }

  private refreshToolbar(): void {
    this.changeDetector.detectChanges();
  }

  setFontSize(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    const chain = this.editor?.chain().focus();
    if (!chain) { return; }

    if (value === 'default') {
      chain.unsetFontSize().run();
    } else {
      chain.setFontSize(value).run();
    }
  }

  setLineHeight(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    const chain = this.editor?.chain().focus();
    if (!chain) { return; }

    if (value === 'default') {
      chain.unsetLineHeight().run();
    } else {
      chain.setLineHeight(value).run();
    }
  }

  setTextColor(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.editor?.chain().focus().setColor(value).run();
    this.activeColorPicker = null;
  }

  setBackgroundColor(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.editor?.chain().focus().setBackgroundColor(value).run();
    this.activeColorPicker = null;
  }

  removeTextColor(): void {
    this.editor?.chain().focus().unsetColor().run();
    this.showMoreTools = false;
    this.activeColorPicker = null;
  }

  removeBackgroundColor(): void {
    this.editor?.chain().focus().unsetBackgroundColor().run();
    this.showMoreTools = false;
    this.activeColorPicker = null;
  }

  toggleColorPicker(type: 'text' | 'background'): void {
    this.activeColorPicker = this.activeColorPicker === type ? null : type;
    this.showMoreTools = false;
  }

  setAlignment(alignment: 'left' | 'center' | 'right'): void {
    this.editor?.chain().focus().setTextAlign(alignment).run();
  }

  toggleBulletList(): void {
    this.editor?.chain().focus().toggleBulletList().run();
    this.showMoreTools = false;
  }

  toggleOrderedList(): void {
    this.editor?.chain().focus().toggleOrderedList().run();
    this.showMoreTools = false;
  }

  toggleBold(): void {
    this.editor?.chain().focus().toggleBold().run();
  }

  toggleItalic(): void {
    this.editor?.chain().focus().toggleItalic().run();
  }

  toggleUnderline(): void {
    this.editor?.chain().focus().toggleUnderline().run();
  }

  setLink(): void {
    if (!this.editor) { return; }

    this.showMoreTools = false;

    const currentLink = this.editor.getAttributes('link').href ?? '';
    const href = window.prompt('Enter URL', currentLink);
    if (href === null) { return; }

    const chain = this.editor.chain().focus();
    if (href.trim()) {
      chain.setLink({ href: href.trim(), target: '_blank' }).run();
    } else {
      chain.unsetLink().run();
    }
  }

  toggleMoreTools(): void {
    this.showMoreTools = !this.showMoreTools;
  }
}
